// src/com/com_info/ComInfo.js
import React, {useEffect, useRef, useState} from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { IconButton, Popover } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'; // 사용
import StopCircleIcon from '@mui/icons-material/StopCircle'; // 사용중지.
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import { ContentConainer, ContentHeader, ControlBox, ToolBox } from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { CircleLoading } from '../../common/CircleLoading';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, comInfoListRepo, userInfoRepo } from '../../value/AppModel';
import {
    LIST_COM_INFO, SET_USAGE_COM_INFO, SET_DC_COM_INFO,
    EDIT_COM_INFO,
    GET_COM_INFO
} from './ComInfoGql';
import { ComInfoDictionary } from './ComInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import ComInfoInput from './ComInfoInput';

const PopTablePaper = styled(Box)({
    position: 'relative', height:'50vh', width:'50vw', maxWidth:880, overflowY:'auto',
});
const PopTableWrapper = styled(Box)({
    position: 'absolute', top:2, left:2, bottom: 2, right: 2,
    display: 'flex', flexDirection: 'column',
});


/**
 * (COM_INFO) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-09-10 13:18:21 using DbdbTap
 */
export default function ComInfo({
    viewServiceTags,
    show = true,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [comListOfDc, setComListOfDc] = useState([]);
    const [comListFree, setComListFree] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    // ##### Call GraphQL to get List #####
    const [getComInfoList, responseList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: ComInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getComInfoItem, responseItem] = useLazyQuery(GET_COM_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: ComInfoDictionary.errorTitle.Get, message: [error.message]})
	});

    const [setDcComInfo, responseDc] = useMutation(SET_DC_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, clientOption) => {onCompleteSetDc(data, clientOption)},
        onError: (error) => addAlert({title: "운송사 물류센터 설정 오류", message: [error.message]})
    });

    const [setUsageComInfo, responseUsage] = useMutation(SET_USAGE_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, clientOption) => {onCompleteSetUsage(data, clientOption)},
        onError: (error) => addAlert({title: "운송사 사용여부 설정 오류", message: [error.message]})
    });
    const [editComInfo, responseEdit] = useMutation( EDIT_COM_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: ComInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );

    const getComInfoListNow = () => {
        getComInfoList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - comInfoListData.when;
        if(diff > 300)
            getComInfoListNow();
    }, []);

    useEffect(()=>{
        setComListOfDc(comInfoListData.records.filter(item=>item.dcCd===sessionInfo.user.dscIdAs));
        setComListFree(comInfoListData.records.filter(item=>item.dcCd===null));
    }, [comInfoListData, sessionInfo.user.dscIdAs]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.comInfoItem) {
            setEditorState({open:true, item:data.comInfoItem});
        }
        else addAlert({title:ComInfoDictionary.errorTitle.Get, message:['지정한 운송사를 찾을 수 없습니다.']});
    };

    const onCompleteSetDc = (data, clientOption) => {
        if(data.comInfoSetDc.ok) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoSetDc.list});
            setAnchorEl(null);
        }
        else addAlert({title:"운송사 물류센터 설정 오류", message:[data.comInfoSetDc.message]});
    };

    const onCompleteSetUsage = (data, clientOption) => {
        if(data.comInfoSetUsage.ok) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoSetUsage.list});
        }
        else addAlert({title:"운송사 사용여부 설정 오류", message:[data.comInfoSetUsage.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.comInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            enqueueSnackbar('운송사 정보가 정상적으로 수정되었습니다.', {variant:AppStyle.mui.success, autoHideDuration:1500});
            getComInfoListNow();
        }
        else addAlert({title:ComInfoDictionary.errorTitle.Edit, message:[data.comInfoEdit.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getComInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{comInfo:item}};
        editComInfo(mutateParam);
    };

    const onRequestEdit = (item) => {
        getComInfoItem({variables:{comId:item.comId}});
    };

    const confirmSetDc = async (item) => {
        const confirm = await openConfirmDialog({
            title: "물류센터와 운송사 관계 설정 추가",
            content: [
                `${item.comName} 운송사를 현재 센터에 설정하시겠습니까?`
            ]
        });

        if(confirm)
            setDcComInfo({variables:{comInfo:{comId:item.comId, dcCd:sessionInfo.user.dscIdAs}}});
    };

    const confirmUnsetDc = async (item) => {
        const confirm = await openConfirmDialog({
            title: "물류센터와 운송사 관계 설정 해제",
            content: [
                `${item.comName} 운송사를 현재 센터에서 설정해제하시겠습니까?`
            ]
        });

        if(confirm)
            setDcComInfo({variables:{comInfo:{comId:item.comId, dcCd:null}}});
    };

    const confirmStopUsing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운송사 사용중지",
            content: [
                `${item.comName} 운송사를 사용중지하시겠습니까?`,
            ]
        });

        if(confirm)
            setUsageComInfo({variables:{comInfo:{comId:item.comId, useYn:'N'}}});
    };

    const confirmReusing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운송사 사용",
            content: [
                `${item.comName} 운송사를 사용하도록 변경하시겠습니까?`,
            ]
        });
        if(confirm) setUsageComInfo({variables:{comInfo:{comId:item.comId, useYn:'Y'}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickPopCandidates = (e) => {
        if(comListFree.length===0) {
            addAlert({
                title: "추가할 운송사 없음",
                message: ['현재 물류센터에 소속되지 않은 운송사가 없습니다.']
            });
            return;
        }
        setAnchorEl(e.currentTarget);
    };

    const onClickToolOnRowIn = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.DELETE: confirmUnsetDc(row); break;
            case AppObject.command.REUSE: confirmReusing(row); break;
            case AppObject.command.STOP_USING: confirmStopUsing(row); break;
            default: return;
        }
    };

    const onClickToolOnRowOut = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.NEW: confirmSetDc(row); break;
            default: return;
        }
    };
    /*
	CALL wps_set_auth_table('pr_com_info_set_usage', '1', v_msg);
	CALL wps_set_auth_table('pr_com_info_set_dc', '1', v_msg);
    */
    const canSetUsage = checkAuth(authTableTree.tree.pr_com_info_set_usage);
    const canSetDc = checkAuth(authTableTree.tree.pr_com_info_set_dc);
    const canEdit = checkAuth(authTableTree.tree.pr_com_info_edit);

    const toolsIn = canSetUsage ? [
        {
            id:AppObject.command.STOP_USING, icon:<StopCircleIcon/>, color: AppStyle.mui.error,
            where:(row)=>row.useYn === "Y",
            title: "사용중지"
        },
        {
            id:AppObject.command.REUSE, icon:<PlayCircleFilledWhiteIcon/>, color: AppStyle.mui.success,
            where:(row)=>row.useYn !== "Y",
            title: "사용"
        },
    ] : [];
    if(canEdit) toolsIn.push({id:AppObject.command.EDIT, icon:<EditIcon/>, title:"편집"});
    if(canSetDc)
        toolsIn.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning, title:"거래 중지"});

    const toolsOut = canSetDc ? [
        {id:AppObject.command.NEW, icon:<AddCircleIcon/>, title: "거래"},
    ] : [];

    // 센터에 소속되지 않은 업체를 추가하는 UI는 Popover로 목록 띄우고 + 버튼으로 추가하는 방식으로 만든다.
    // use com_info list if belongToDc is true
    const rootStyle = show ? undefined : {display:'none', visibility:'hidden', pointerEvents:'none'};

    const loadingAny = responseList.loading || responseDc.loading || responseUsage.loading || responseItem.loading;

    return(
        <ContentConainer style={rootStyle}>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny
                        ? <CircleLoading />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canSetDc &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickPopCandidates}
                            title="운송사 추가"
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={()=>setAnchorEl(null)}
                        anchorOrigin={{vertical:'bottom', horizontal:'right'}}
                        transformOrigin={{vertical:'top', horizontal:'right'}}
                    >
                        <PopTablePaper>
                            <PopTableWrapper>
                                <Box style={{padding:10, backgroundColor: AppStyle.SuccessRGB, color:'white'}}>
                                    추가할 운송사를 선택해 주세요.
                                </Box>
                                <FlexyTable
                                    name={ComInfoDictionary.freeTableProps.tableId}
                                    tableProps={ComInfoDictionary.freeTableProps}
                                    records={comListFree}
                                    tools={toolsOut}
                                    onClickTool={onClickToolOnRowOut}
                                />
                            </PopTableWrapper>
                        </PopTablePaper>
                    </Popover>
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={ComInfoDictionary.tableProps.tableId}
                tableProps={ComInfoDictionary.tableProps}
                records={comListOfDc}
                search={searchText}
                tools={toolsIn}
                onClickTool={onClickToolOnRowIn}
            />
            <ComInfoInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={responseEdit}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}


/*

	운송사코드
	운송사
	업종 p_bill_job_type
	업종 분류 p_bill_job_class
	운송사 별칭 p_com_alias
	대표자명 p_repr_name
	전화번호 p_office_tel
	FAX p_fax_tel
	우편번호 (주소검색) p_zip_cd
	주소 p_addr
	[담당자 정보]
	담당자 p_agent_name
	연락처 p_agent_tel_no
	이메일 p_agent_email

*/
